import React, { useState } from 'react';
import './GuideStyles.css';

const HowToIntegrateZoom = ({ onBack }) => {
    const [modalImage, setModalImage] = useState(null);

    const handleImageClick = (src, alt) => {
        setModalImage({ src, alt });
    };

    const closeModal = () => {
        setModalImage(null);
    };

    return (
        <div className="guide-container">
            <button className="back-button" onClick={onBack}>
                ← Back to Documentation
            </button>

            <div className="guide-header">
                <h1>How to Integrate Zoom with ClientBloom</h1>
                <p className="guide-subtitle">Step-by-step guide to connect your Zoom account</p>
            </div>

            <div className="guide-content">
                <div className="guide-step">
                    <div className="step-content">
                        <h3>Step 1: Access the Admin Panel</h3>
                        <p>Log in to ClientBloom and navigate to the <strong>Admin Panel</strong> from the sidebar menu.</p>
                        <div className="screenshot-placeholder">
                            <img 
                                src="go_admin_panel.png" 
                                alt="Go to Admin Panel" 
                                onClick={() => handleImageClick('go_admin_panel.png', 'Go to Admin Panel')}
                                className="clickable-image"
                            />
                        </div>
                    </div>
                </div>

                <div className="guide-step">
                    <div className="step-content">
                        <h3>Step 2: Navigate to Integrations</h3>
                        <p>In the Admin Panel, click on the <strong>"Integrations"</strong> tab at the top.</p>
                        <div className="screenshot-placeholder">
                            <img 
                                src="admin_panel.png" 
                                alt="Admin Panel" 
                                onClick={() => handleImageClick('admin_panel.png', 'Admin Panel')}
                                className="clickable-image"
                            />
                        </div>
                    </div>
                </div>

                <div className="guide-step">
                    <div className="step-content">
                        <h3>Step 3: Find the Zoom Integration</h3>
                        <p>Scroll down until you find the <strong>"Connect your Zoom Account"</strong> card.</p>
                        <div className="screenshot-placeholder">
                            <img 
                                src="search_zoom.PNG" 
                                alt="Search Zoom Integration" 
                                onClick={() => handleImageClick('search_zoom.PNG', 'Search Zoom Integration')}
                                className="clickable-image"
                            />
                        </div>
                    </div>
                </div>

                <div className="guide-step">
                    <div className="step-content">
                        <h3>Step 4: Click "Connect"</h3>
                        <p>Press the blue <strong>"Connect"</strong> button on the Zoom card. This will redirect you to Zoom's authorization page.</p>
                        <div className="screenshot-placeholder">
                            <img 
                                src="conect_zoom.PNG" 
                                alt="Connect Zoom Integration" 
                                onClick={() => handleImageClick('conect_zoom.PNG', 'Connect Zoom Integration')}
                                className="clickable-image"
                            />
                        </div>
                    </div>
                </div>

                <div className="guide-step">
                    <div className="step-content">
                        <h3>Step 5: Authorize on Zoom</h3>
                        <p>On the Zoom page, review the requested permissions and click <strong>"Authorize"</strong> to allow ClientBloom to access your recordings.</p>
                        <div className="screenshot-placeholder">
                            <img 
                                src="zoom_permission.PNG" 
                                alt="Permissions Zoom Integration" 
                                onClick={() => handleImageClick('zoom_permission.PNG', 'Permissions Zoom Integration')}
                                className="clickable-image"
                            />
                        </div>
                        <div className="info-box">
                            <strong>ℹ️ Requested Permissions:</strong>
                            <ul>
                                <li>Access to cloud recordings</li>
                                <li>Read meeting information</li>
                                <li>Read transcriptions</li>
                            </ul>
                        </div>
                    </div>
                </div>

                <div className="guide-step">
                    <div className="step-content">
                        <h3>Step 6: Successful Connection</h3>
                        <p>After authorizing, you'll be redirected back to ClientBloom. The Zoom integration card will now display two buttons: <strong>"Disconnect"</strong> and <strong>"Manage"</strong>, confirming that the connection was successful.</p>
                        <div className="screenshot-placeholder">
                            <img 
                                src="zoom_connected.PNG" 
                                alt="Zoom Connected Integration" 
                                onClick={() => handleImageClick('zoom_connected.PNG', 'Zoom Connected Integration')}
                                className="clickable-image"
                            />
                        </div>
                        <div className="success-box">
                            <strong>✅ Done!</strong> Your Zoom account is connected. Recordings will begin syncing automatically.
                        </div>
                        <div className="info-box">
                            <strong>ℹ️ Available Actions:</strong>
                            <ul>
                                <li><strong>Disconnect:</strong> Remove the Zoom integration from your workspace</li>
                                <li><strong>Manage:</strong> Access settings to manually process transcripts and view sync history</li>
                            </ul>
                        </div>
                    </div>
                </div>

                <div className="guide-section">
                    <h2>Prerequisites</h2>
                    <div className="requirements-box">
                        <ul>
                            <li><strong>Be a workspace administrator</strong> in ClientBloom</li>
                            <li><strong>Have a Zoom account</strong> with cloud recordings enabled</li>
                            <li><strong>Automatic transcriptions</strong> enabled in Zoom</li>
                            <li><strong>Clients created</strong> in ClientBloom with emails matching Zoom participants</li>
                        </ul>
                    </div>
                </div>

                <div className="guide-section">
                    <h2>Troubleshooting</h2>
                    <div className="troubleshooting-box">
                        <div className="trouble-item">
                            <strong>❌ Authorization fails</strong>
                            <p>Verify you're using the correct Zoom account and have administrator permissions.</p>
                        </div>
                        <div className="trouble-item">
                            <strong>❌ Don't see synced calls</strong>
                            <p>Make sure cloud recordings are enabled and transcriptions have been generated in Zoom.</p>
                        </div>
                        <div className="trouble-item">
                            <strong>❌ Calls don't match with clients</strong>
                            <p>Verify that Zoom participant emails exactly match client emails in ClientBloom.</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Image Modal */}
            {modalImage && (
                <div className="image-modal" onClick={closeModal}>
                    <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close" onClick={closeModal}>×</button>
                        <img src={modalImage.src} alt={modalImage.alt} />
                        <p className="modal-caption">{modalImage.alt}</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HowToIntegrateZoom;
